import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';
import { PaginatorState } from 'primeng/paginator';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';

import { EnumService } from '@shared/api/services/enum.service';
import { PermissaoService } from '@core/services/permissao.service';
import { AccaoKey, CelulaPermissao, Grupo, LinhaModulo } from '../permissao.model';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import {
  construirLinhas,
  derivarOpcoesKeycloakRole,
  todasAsCelulas,
  todosOsRoleEnums,
} from '../permissao-matriz.util';

type PermissionActionFilter = 'TODAS' | AccaoKey | 'OUTRAS';
type PermissionAssignmentFilter = 'TODAS' | 'ATRIBUIDAS' | 'NAO_ATRIBUIDAS' | 'COMPLETAS';

@Component({
  selector: 'app-grupo-permissoes-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DrawerModule,
    ButtonModule,
    CheckboxModule,
    SkeletonModule,
    PaginatorControlComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './grupo-permissoes-drawer.component.html',
  styleUrl: './grupo-permissoes-drawer.component.scss',
})
export class GrupoPermissoesDrawerComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() grupo: Grupo | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  private readonly enumSvc = inject(EnumService);
  private readonly permissaoSvc = inject(PermissaoService);
  private readonly msg = inject(MessageService);
  private readonly confirmSvc = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  /** Linhas derivadas do enum — não dependem do grupo, calculadas uma única vez e reutilizadas em cada abertura. */
  private linhasCache: LinhaModulo[] | null = null;
  private valorPorChave = new Map<string, number>();
  private todosRoleEnums: number[] = [];
  /** Todas as células (fixas + outras) de todas as linhas — para actualizar `.checked` sem método no template. */
  private todasAsCelulasCache: CelulaPermissao[] = [];

  linhas: LinhaModulo[] = [];
  linhasPaginadas: LinhaModulo[] = [];
  totalOpcoes = 0;
  totalLinhasFiltradas = 0;
  permissionPage = 1;
  permissionPageSize = 5;
  permissionFirst = 0;
  permissionSearch = '';
  permissionActionFilter: PermissionActionFilter = 'TODAS';
  permissionAssignmentFilter: PermissionAssignmentFilter = 'TODAS';
  permissionFiltersCount = 0;
  showPermissionFiltersDrawer = false;

  /** valor (índice do enum) -> roleId (guid) das roles actualmente atribuídas no servidor. */
  private atribuidas = new Map<number, string>();
  /** Estado local pendente — só é gravado quando o utilizador clica "Guardar alterações". */
  private pendente = new Set<number>();

  carregando = false;
  salvando = false;
  erro = false;

  // ── Estado derivado, pré-computado em recalcularEstadoDerivado() — nunca via getter/método do template ──
  pendenteCount = 0;
  temAlteracoesPendentes = false;
  estadoGlobalChecked = false;
  estadoGlobalIndeterminate = false;
  estadoGlobalLabel: 'Marcar todas' | 'Desmarcar todas' = 'Marcar todas';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.grupo) {
      this.resetPermissionView();
      this.carregarDados();
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.carregarDados();
  }

  private carregarDados(): void {
    if (!this.grupo) return;
    this.carregando = true;
    this.erro = false;
    this.cdr.markForCheck();

    const enum$ = this.linhasCache
      ? of({} as Record<string, string>)
      : this.enumSvc.getEnum('KeycloakRole');

    forkJoin({
      enumRecord: enum$,
      roles: this.permissaoSvc.listarPermissoesDoGrupo(this.grupo.id),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.carregando = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ enumRecord, roles }) => {
          if (!this.linhasCache) {
            const opcoes = derivarOpcoesKeycloakRole(enumRecord);
            this.linhasCache = construirLinhas(opcoes);
            this.valorPorChave = new Map(opcoes.map((o) => [o.chave, o.valor]));
            this.todosRoleEnums = todosOsRoleEnums(this.linhasCache);
            this.totalOpcoes = this.todosRoleEnums.length;
            this.todasAsCelulasCache = todasAsCelulas(this.linhasCache);
          }
          this.linhas = this.linhasCache;

          const atribuidas = new Map<number, string>();
          for (const r of roles.data || []) {
            const valor = this.valorPorChave.get(r.nome);
            if (valor !== undefined) atribuidas.set(valor, r.id);
          }
          this.atribuidas = atribuidas;
          this.pendente = new Set(atribuidas.keys());
          this.recalcularEstadoDerivado();
        },
        error: (err) => {
          console.error('Erro ao carregar permissões do grupo:', err);
          this.erro = true;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Recalcula todo o estado derivado do estado pendente (contador do header,
   * tri-state global, tri-state por linha) e guarda o resultado em
   * propriedades simples. Chamado explicitamente após cada alteração — nunca
   * substituído por um getter/método invocado do template (com OnPush, isso
   * corre a cada ciclo de change detection).
   */
  private recalcularEstadoDerivado(): void {
    this.pendenteCount = this.pendente.size;
    this.temAlteracoesPendentes = this.calcularTemAlteracoes();

    if (this.pendenteCount === 0) {
      this.estadoGlobalChecked = false;
      this.estadoGlobalIndeterminate = false;
    } else if (this.pendenteCount === this.totalOpcoes) {
      this.estadoGlobalChecked = true;
      this.estadoGlobalIndeterminate = false;
    } else {
      this.estadoGlobalChecked = false;
      this.estadoGlobalIndeterminate = true;
    }
    this.estadoGlobalLabel = this.estadoGlobalChecked ? 'Desmarcar todas' : 'Marcar todas';

    for (const linha of this.linhas) {
      const marcadas = linha.roles.filter((v) => this.pendente.has(v)).length;
      linha.checked = linha.roles.length > 0 && marcadas === linha.roles.length;
      linha.indeterminate = marcadas > 0 && marcadas < linha.roles.length;
    }

    for (const celula of this.todasAsCelulasCache) {
      celula.checked = this.pendente.has(celula.roleEnum);
    }

    this.recalcularLinhasVisiveis();
    this.cdr.markForCheck();
  }

  onPermissionSearch(value: string): void {
    this.permissionSearch = value;
    this.recalcularLinhasVisiveis(true);
  }

  onPermissionActionFilter(value: string): void {
    this.permissionActionFilter = value as PermissionActionFilter;
    this.recalcularLinhasVisiveis(true);
  }

  onPermissionAssignmentFilter(value: string): void {
    this.permissionAssignmentFilter = value as PermissionAssignmentFilter;
    this.recalcularLinhasVisiveis(true);
  }

  onPermissionPageChange(event: PaginatorState): void {
    this.permissionPage = (event.page ?? 0) + 1;
    this.permissionPageSize = event.rows ?? this.permissionPageSize;
    this.recalcularLinhasVisiveis();
  }

  clearPermissionFilters(): void {
    this.permissionSearch = '';
    this.permissionActionFilter = 'TODAS';
    this.permissionAssignmentFilter = 'TODAS';
    this.recalcularLinhasVisiveis(true);
  }

  clearPermissionSearch(): void {
    this.permissionSearch = '';
    this.recalcularLinhasVisiveis(true);
  }

  clearAdvancedPermissionFilters(): void {
    this.permissionActionFilter = 'TODAS';
    this.permissionAssignmentFilter = 'TODAS';
    this.recalcularLinhasVisiveis(true);
  }

  private resetPermissionView(): void {
    this.permissionSearch = '';
    this.permissionActionFilter = 'TODAS';
    this.permissionAssignmentFilter = 'TODAS';
    this.permissionPage = 1;
    this.permissionFirst = 0;
    this.permissionFiltersCount = 0;
    this.showPermissionFiltersDrawer = false;
  }

  private recalcularLinhasVisiveis(resetPage = false): void {
    if (resetPage) this.permissionPage = 1;
    const search = this.normalizeText(this.permissionSearch);

    const filtradas = this.linhas.filter((linha) => {
      const marcadas = linha.roles.filter((role) => this.pendente.has(role)).length;
      const matchesAssignment =
        this.permissionAssignmentFilter === 'TODAS' ||
        (this.permissionAssignmentFilter === 'ATRIBUIDAS' && marcadas > 0) ||
        (this.permissionAssignmentFilter === 'NAO_ATRIBUIDAS' && marcadas === 0) ||
        (this.permissionAssignmentFilter === 'COMPLETAS' && marcadas === linha.roles.length);
      if (!matchesAssignment) return false;

      const matchesAction =
        this.permissionActionFilter === 'TODAS' ||
        (this.permissionActionFilter === 'OUTRAS' && linha.outras.length > 0) ||
        (this.permissionActionFilter !== 'OUTRAS' &&
          !!linha.celulas[this.permissionActionFilter]);
      if (!matchesAction) return false;

      if (!search) return true;
      const permissionLabels = [
        ...Object.values(linha.celulas)
          .filter((celula): celula is CelulaPermissao => !!celula)
          .flatMap((celula) => [celula.chave, celula.ariaLabel]),
        ...linha.outras.flatMap((celula) => [celula.chave, celula.ariaLabel, celula.label]),
      ];
      return this.normalizeText(`${linha.modulo} ${permissionLabels.join(' ')}`).includes(search);
    });

    this.permissionFiltersCount =
      Number(this.permissionActionFilter !== 'TODAS') +
      Number(this.permissionAssignmentFilter !== 'TODAS');
    this.totalLinhasFiltradas = filtradas.length;
    const totalPages = Math.max(1, Math.ceil(filtradas.length / this.permissionPageSize));
    this.permissionPage = Math.min(this.permissionPage, totalPages);
    this.permissionFirst = (this.permissionPage - 1) * this.permissionPageSize;
    this.linhasPaginadas = filtradas.slice(
      this.permissionFirst,
      this.permissionFirst + this.permissionPageSize,
    );
  }

  private normalizeText(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  private calcularTemAlteracoes(): boolean {
    if (this.pendente.size !== this.atribuidas.size) return true;
    for (const valor of this.pendente) {
      if (!this.atribuidas.has(valor)) return true;
    }
    return false;
  }

  toggle(valor: number): void {
    if (this.pendente.has(valor)) {
      this.pendente.delete(valor);
    } else {
      this.pendente.add(valor);
    }
    this.recalcularEstadoDerivado();
  }

  /** Toggle tri-state por módulo: nenhuma/parcial -> marca todas as roles da linha; todas -> desmarca. */
  toggleLinha(linha: LinhaModulo): void {
    if (linha.checked) {
      for (const v of linha.roles) this.pendente.delete(v);
    } else {
      for (const v of linha.roles) this.pendente.add(v);
    }
    this.recalcularEstadoDerivado();
  }

  /** Toggle tri-state global: nenhuma/parcial -> marca todas as 37 roles (incl. outras); todas -> desmarca. */
  toggleTodas(): void {
    if (this.estadoGlobalChecked) {
      this.pendente.clear();
    } else {
      this.pendente = new Set(this.todosRoleEnums);
    }
    this.recalcularEstadoDerivado();
  }

  salvarAlteracoes(): void {
    if (!this.grupo || this.salvando) return;

    const paraAdicionar = [...this.pendente].filter((v) => !this.atribuidas.has(v));
    const paraRemover = [...this.atribuidas.keys()].filter((v) => !this.pendente.has(v));

    if (paraAdicionar.length === 0 && paraRemover.length === 0) return;

    const executar = () => this.executarSalvar(paraAdicionar, paraRemover);

    if (paraRemover.length > 0) {
      this.confirmSvc.confirm({
        header: 'Remover permissões',
        message: `Vais remover ${paraRemover.length} permissão(ões) deste grupo. Continuar?`,
        acceptLabel: 'Sim, remover',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-danger',
        accept: executar,
      });
    } else {
      executar();
    }
  }

  private executarSalvar(paraAdicionar: number[], paraRemover: number[]): void {
    if (!this.grupo) return;
    this.salvando = true;
    this.cdr.markForCheck();

    const grupoId = this.grupo.id;
    const chamadas: Observable<{ valor: number; ok: boolean }>[] = [
      ...paraAdicionar.map((valor) =>
        this.permissaoSvc.adicionarRoleAoGrupo(grupoId, valor).pipe(
          map(() => ({ valor, ok: true })),
          catchError(() => of({ valor, ok: false })),
        ),
      ),
      ...paraRemover.map((valor) =>
        this.permissaoSvc
          .removerRoleDoGrupo(grupoId, this.atribuidas.get(valor)!)
          .pipe(
            map(() => ({ valor, ok: true })),
            catchError(() => of({ valor, ok: false })),
          ),
      ),
    ];

    forkJoin(chamadas)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resultados) => {
        const falhas = resultados.filter((r) => !r.ok).length;

        // Nunca assumir que o estado local é a verdade — recarregar sempre do servidor.
        this.carregarDados();
        this.salvando = false;

        if (falhas > 0) {
          this.msg.add({
            severity: 'warn',
            summary: 'Guardado parcialmente',
            detail: `${falhas} alteração(ões) falharam. A mostrar o estado real do servidor.`,
            life: 6000,
          });
        } else {
          this.msg.add({
            severity: 'success',
            summary: 'Guardado',
            detail: 'Permissões actualizadas com sucesso.',
            life: 3000,
          });
        }
        this.cdr.markForCheck();
      });
  }

  fechar(): void {
    this.showPermissionFiltersDrawer = false;
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onHide(): void {
    this.showPermissionFiltersDrawer = false;
    this.visible = false;
    this.visibleChange.emit(false);
  }

  trackByModulo(_index: number, linha: LinhaModulo): string {
    return linha.modulo;
  }

  trackByValor(_index: number, item: { roleEnum: number }): number {
    return item.roleEnum;
  }
}
